import "datatables.net-dt/js/dataTables.dataTables.js";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from 'react-toastify';
import { Modal, Popover } from 'antd';
import { useNavigate } from "react-router-dom";
import { api } from "../../utils/apiClient";
import { useSelector } from "react-redux";
import { API } from "../../config/constants";
import Pagination from "../../utils/Pagination";
import { formatDateToDMY } from "../../utils/tools";
import ContentLoader from "../../utils/ContentLoader";
import { useState } from "react";
import SearchBy from "../../utils/SearchBy";
import { TableSortHeaders } from "../../utils/TableSortHeaders";
import ImagePreviewModal from "../../utils/ImagePreviewModal";

const { confirm } = Modal;

const UserTable = ({
    users,
    roles,
    onUserDeleted,
    onEdit,
    page,
    setPage,
    totalPages,
    totalEntries,
    sort,
    order,
    setOrder,
    setSort,
    search,
    setSearch,
    entriesPerPage,
    setEntriesPerPage
}) => {
    const viewPermissions = useSelector((state) => state.permission.rolePermissions) || [];
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const headerConfig = [
        { key: "id", label: "User ID", sortable: false },
        { key: "created_at", label: "Created Date", sortable: false },
        { key: "avatar", label: "Avatar", sortable: false },
        { key: "name", label: "Full Name", sortable: false },
        { key: "email", label: "Email", sortable: false },
        { key: "role", label: "Role Name", sortable: false },
        { key: "status", label: "Status", sortable: false },
        { key: "action", label: "Action", sortable: false }
    ];

    const handleDelete = async (userId) => {
        try {
            setLoading(true);
            await api.delete(`/users/${userId}`);
            toast.success("User deleted successfully!");
            if (onUserDeleted) onUserDeleted(userId);
        } catch (error) {
            console.error("Error deleting user:", error);
            toast.error("Failed to delete user: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };
    const showDeleteConfirm = (userId) => {
        confirm({
            title: 'Are you sure you want to delete?',
            okText: 'Yes',
            okType: 'primary',
            cancelText: 'No',
            centered: true,
            onOk() { handleDelete(userId); },
        });
    };

    if (loading) return <ContentLoader />;

    return (
        <div className="card basic-data-table text-sm overflow-x-auto">
            <div className="card-header d-flex justify-content-end align-items-center global-header">
                <SearchBy
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                />
                {viewPermissions.includes("user.create") && (
                    <button
                        className="btn btn-primary-600 global-add-btn"
                        type="button"
                        onClick={() => navigate("/create-user", { state: roles })}
                    >
                        <Icon
                            icon="fa6-regular:square-plus"
                            className="icon text-lg line-height-1"
                        />
                        Add
                    </button>
                )}
            </div>
            <div className="card-body card-body-modify">
                <div className="w-full overflow-x-auto">
                    <table className="table bordered-table mb-0 text-sm w-full min-w-max" id="dataTable">
                        <thead>
                            <tr>
                                {TableSortHeaders(headerConfig, sort, order, setSort, setOrder, setPage)}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(users) && users.length > 0 ? (
                                users.map((user) => (
                                    <tr key={user.id}>
                                        <td>{user.id}</td>
                                        <td>{formatDateToDMY(user.created_at)}</td>
                                        <td>
                                            <ImagePreviewModal
                                                src={`${API.IMAGE_BASE_URL}/${user.icon}`}
                                                fallback="/assets/images/logo-profile.png"
                                                alt={user.name}
                                                thumbWidth={50}
                                                thumbHeight={50}
                                            />
                                        </td>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                        <td>
                                            <span className="bg-primary-light text-primary-600 px-24 py-4 rounded-pill fw-medium text-sm">
                                                {user.role.name}
                                            </span>
                                        </td>

                                        <td>
                                            <span
                                                className={
                                                    user.active
                                                        ? "bg-success-focus text-success-main px-24 py-4 rounded-pill fw-medium text-sm"
                                                        : "bg-danger-focus text-danger-main px-24 py-4 rounded-pill fw-medium text-sm"
                                                }
                                            >
                                                {user.active ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td >
                                            <Popover
                                                trigger="click"
                                                placement="bottomRight"
                                                overlayClassName="action-popover"
                                                content={
                                                    <div className="action-list">
                                                        {/* EDIT */}
                                                        {viewPermissions.includes("user.edit") && (
                                                            <button
                                                                onClick={() => onEdit(user)}
                                                                className="action-item text-success-main"
                                                            >
                                                                <Icon icon="lucide:edit" />
                                                                <span>Edit</span>
                                                            </button>
                                                        )}
                                                        {/* DELETE */}
                                                        {viewPermissions.includes("user.delete") && (
                                                            <button
                                                                className="action-item text-danger-main"
                                                                onClick={() => showDeleteConfirm(user.id)}
                                                            >
                                                                <Icon icon="mingcute:delete-2-line" />
                                                                <span>Delete</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className="w-32-px h-32-px bg-neutral-300 rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                                                >
                                                    <Icon icon="mdi:dots-vertical" />
                                                </button>
                                            </Popover>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="text-center">
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {console.log("setEntriesPerPage:", setEntriesPerPage)}
                {/* <Pagination
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    totalEntries={totalEntries}
                    entriesPerPage={entriesPerPage}
                    setEntriesPerPage={setEntriesPerPage}
                /> */}
            </div>
        </div>
    );
};

export default UserTable;